/**
 * OaSIS Explorer Frontend - Sorting Utilities
 * 
 * Shared sorting logic for occupation results across different views
 */

/**
 * Sort order type
 */
export type SortOrder = 'asc' | 'desc';

/**
 * Sort field type
 */
export type SortField = 'title' | 'code';

/**
 * Occupation result type (minimal interface for sorting)
 */
export interface SortableOccupation {
  oasisCode: string;
  title: string;
  [key: string]: unknown; // Allow additional properties
}

/**
 * Sort occupation results by title or code
 * 
 * @param results - Array of occupation results to sort
 * @param sortBy - Field to sort by ('title' or 'code')
 * @param sortOrder - Sort order ('asc' or 'desc')
 * @returns Sorted array (does not mutate original)
 * 
 * @example
 * ```typescript
 * const sorted = sortOccupations(results, 'title', 'asc');
 * ```
 */
export function sortOccupations<T extends SortableOccupation>(
  results: T[],
  sortBy: SortField,
  sortOrder: SortOrder
): T[] {
  if (!results.length) return [];
  
  const sorted = [...results];
  
  if (sortBy === 'title') {
    sorted.sort((a, b) => {
      const comparison = a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  } else if (sortBy === 'code') {
    sorted.sort((a, b) => {
      const comparison = a.oasisCode.localeCompare(b.oasisCode);
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }
  
  return sorted;
}

/**
 * Parse sort parameter from URL format (e.g., "title-asc", "code-desc")
 * 
 * @param sortParam - Sort parameter string from URL (default: "title-asc")
 * @returns Object with sortBy and sortOrder
 * 
 * @example
 * ```typescript
 * const { sortBy, sortOrder } = parseSortParam(searchParams.get('sort'));
 * ```
 */
export function parseSortParam(sortParam: string | null): {
  sortBy: SortField;
  sortOrder: SortOrder;
} {
  const param = sortParam || 'title-asc';
  const [sortBy, sortOrder] = param.split('-') as [SortField, SortOrder];
  
  // Validate and provide defaults
  const validSortBy = sortBy === 'title' || sortBy === 'code' ? sortBy : 'title';
  const validSortOrder = sortOrder === 'asc' || sortOrder === 'desc' ? sortOrder : 'asc';
  
  return {
    sortBy: validSortBy,
    sortOrder: validSortOrder,
  };
}

/**
 * Create sort parameter string for URL (e.g., "title-asc", "code-desc")
 * 
 * @param sortBy - Field to sort by
 * @param sortOrder - Sort order
 * @returns URL-formatted sort parameter string
 * 
 * @example
 * ```typescript
 * const sortParam = createSortParam('title', 'desc'); // "title-desc"
 * ```
 */
export function createSortParam(sortBy: SortField, sortOrder: SortOrder): string {
  return `${sortBy}-${sortOrder}`;
}

